import React, { useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import {
  Loader2, X, ChevronLeft, User, Building2, FileText, Phone, Mail, MapPin,
  Sparkles, Plus, Trash2, Archive as ArchiveIcon, RotateCcw, StickyNote,
  Calendar, ShieldCheck, MessageCircle, CalendarClock, DollarSign, MoreHorizontal,
} from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Sheet, SheetContent, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

import {
  getContractPatient, updateContractPatient, addContractPatientNote,
  archiveContractPatient, restoreContractPatient,
  binContractPatient, unbinContractPatient, deleteContractPatient,
  listPatientAppointments,
} from "@/lib/api";
import StandingOrdersPanel from "@/components/clients/StandingOrdersPanel";
import { useAiContext } from "@/lib/aiContext";

const fmtDate = (v) => { if (!v) return "—"; try { return format(new Date(v), "PP"); } catch { return v; } };
const fmtDateTime = (v) => { if (!v) return "—"; try { return format(new Date(v), "PPp"); } catch { return v; } };

const GENDER_LABEL = {
  male: "Male", female: "Female", other: "Other", prefer_not_to_say: "Prefer not to say",
};

const ageFromDob = (dob) => {
  if (!dob) return null;
  try {
    const d = new Date(dob); if (isNaN(d)) return null;
    const now = new Date();
    let age = now.getFullYear() - d.getFullYear();
    const m = now.getMonth() - d.getMonth();
    if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age--;
    return age;
  } catch { return null; }
};

export default function ContractPatientProfile({ patientId, onClose, onUpdate, onDelete }) {
  const [loading, setLoading] = useState(true);
  const [record, setRecord] = useState(null);
  const [tab, setTab] = useState("overview");
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [savingField, setSavingField] = useState(false);
  const [note, setNote] = useState("");
  const [savingNote, setSavingNote] = useState(false);

  // Scope the floating AI Assistant to this patient while their sheet is open.
  const aiCtx = useAiContext();
  useEffect(() => {
    if (!patientId) return;
    aiCtx.set({
      page: "clients", entity_type: "contract_patient", entity_id: patientId,
      label: record?.full_name ? `Patient ${record.full_name}` : "This contract patient",
    });
    return () => {
      aiCtx.set({ page: "clients", entity_type: null, entity_id: null, label: "Clients directory" });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId, record?.full_name]);

  useEffect(() => {
    if (!patientId) return;
    let cancelled = false;
    setLoading(true);
    setTab("overview");
    setEditing(false);
    getContractPatient(patientId)
      .then((doc) => {
        if (cancelled) return;
        setRecord(doc);
        setForm({
          full_name: doc.full_name || "",
          phone: doc.phone || "",
          email: doc.email || "",
          address: doc.address || "",
          dob: doc.dob || "",
          mrn: doc.mrn || "",
          gender: doc.gender || "",
          primary_language: doc.primary_language || "English",
          allergies: doc.allergies || "",
          fall_risk: !!doc.fall_risk,
          mobility_notes: doc.mobility_notes || "",
          notes: doc.notes || "",
          primary_facility_contact_name: doc.primary_facility_contact_name || "",
          primary_facility_contact_phone: doc.primary_facility_contact_phone || "",
          assigned_staff_name: doc.assigned_staff_name || "",
          authorized_services: (doc.authorized_services || []).join(", "),
          contract_number: doc.contract_number || "",
          status: doc.status || "active",
        });
      })
      .catch(() => toast.error("Failed to load patient."))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [patientId]);

  const saveEdits = async () => {
    setSavingField(true);
    try {
      const body = { ...form };
      body.authorized_services = form.authorized_services
        ? form.authorized_services.split(",").map((s) => s.trim()).filter(Boolean)
        : [];
      body.gender = body.gender || null;
      const updated = await updateContractPatient(patientId, body);
      setRecord(updated);
      setEditing(false);
      toast.success("Patient updated.");
      onUpdate?.(updated);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    } finally { setSavingField(false); }
  };

  const submitNote = async () => {
    if (!note.trim()) return;
    setSavingNote(true);
    try {
      const updated = await addContractPatientNote(patientId, note.trim());
      setRecord(updated); setNote("");
      toast.success("Note added.");
      onUpdate?.(updated);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Note save failed.");
    } finally { setSavingNote(false); }
  };

  const runLifecycle = async (action) => {
    try {
      const fn = { archive: archiveContractPatient, restore: restoreContractPatient,
                   bin: binContractPatient, unbin: unbinContractPatient }[action];
      const updated = await fn(patientId);
      setRecord(updated);
      onUpdate?.(updated);
      toast.success({ archive: "Archived.", restore: "Restored.",
                      bin: "Moved to bin.", unbin: "Restored from bin." }[action]);
    } catch (e) { toast.error(e?.response?.data?.detail || "Action failed."); }
  };

  const runDelete = async () => {
    if (!window.confirm("Permanently delete this patient? This cannot be undone.")) return;
    try {
      await deleteContractPatient(patientId);
      toast.success("Patient permanently deleted.");
      onDelete?.(patientId);
      onClose?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed."); }
  };

  const initials = useMemo(() => {
    const n = (record?.full_name || "").trim();
    if (!n) return "•";
    const parts = n.split(/\s+/);
    return (parts[0][0] + (parts[1]?.[0] || "")).toUpperCase();
  }, [record?.full_name]);

  const facility = record?.facility;
  const contract = record?.contract;

  return (
    <Sheet open={!!patientId} onOpenChange={(o) => !o && onClose()}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-[520px] lg:max-w-[600px] p-0 flex flex-col overflow-hidden"
        data-testid="contract-patient-detail-panel"
      >
        <SheetTitle className="sr-only">Contract patient — {record?.full_name || ""}</SheetTitle>
        <SheetDescription className="sr-only">Full patient profile with Overview, Contacts, and Contract Details tabs.</SheetDescription>

        {loading || !record ? (
          <div className="flex-1 grid place-items-center">
            <Loader2 className="h-6 w-6 animate-spin" style={{ color: "#0B2545" }} />
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="px-5 py-4 border-b" style={{ borderColor: "#F1F5F9" }}>
              <div className="flex items-start justify-between gap-2">
                <button onClick={onClose} className="text-[11px] inline-flex items-center gap-1"
                        style={{ color: "#64748B" }} data-testid="patient-detail-back">
                  <ChevronLeft className="h-3.5 w-3.5" /> Back
                </button>
                <button onClick={onClose} className="h-7 w-7 grid place-items-center rounded-md hover:bg-[#F1F5F9]"
                        data-testid="patient-detail-close">
                  <X className="h-4 w-4" style={{ color: "#475569" }} />
                </button>
              </div>
              <div className="mt-2 flex items-start gap-3">
                <div className="h-12 w-12 grid place-items-center rounded-full shrink-0 text-sm font-bold"
                     style={{ background: "rgba(15,109,93,0.14)", color: "#0F6D5D" }}>
                  {initials}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-display text-xl font-semibold truncate" style={{ color: "#0B2545" }}
                       data-testid="patient-detail-name">
                    {record.full_name}
                  </div>
                  <div className="mt-1 flex flex-wrap items-center gap-1.5">
                    <span className="inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider"
                          style={{ background: "rgba(212,175,55,0.16)", color: "#7A5E12", borderColor: "rgba(212,175,55,0.4)" }}
                          data-testid="patient-badge-contract">
                      Contract Patient
                    </span>
                    <span className="inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold"
                          style={{ background: record.status === "active" ? "rgba(15,109,93,0.10)" : "#F1F5F9",
                                   color: record.status === "active" ? "#0F6D5D" : "#475569",
                                   borderColor: "#E2E8F0" }}>
                      {(record.status || "active").toUpperCase()}
                    </span>
                    {record.is_binned ? (
                      <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold uppercase"
                            style={{ background: "rgba(220,38,38,0.12)", color: "#991B1B" }}>Bin</span>
                    ) : record.is_archived ? (
                      <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold uppercase"
                            style={{ background: "rgba(148,163,184,0.2)", color: "#475569" }}>Archived</span>
                    ) : null}
                  </div>
                  <div className="mt-1.5 text-[11px] flex flex-wrap gap-x-2 gap-y-0.5" style={{ color: "#64748B" }}>
                    <span className="inline-flex items-center gap-1">
                      <Building2 className="h-3 w-3" /> {record.facility_name || "No facility"}
                    </span>
                    {record.contract_number ? (
                      <span className="inline-flex items-center gap-1">
                        <FileText className="h-3 w-3" /> Contract #{record.contract_number}
                      </span>
                    ) : null}
                  </div>
                </div>
              </div>

              {/* Quick actions */}
              <div className="mt-4 flex flex-wrap gap-1.5" data-testid="patient-quick-actions">
                <QuickBtn icon={CalendarClock} label="Schedule" testId="qa-schedule"
                          onClick={() => setTab("standing")} />
                <QuickBtn icon={MessageCircle} label="Message facility" testId="qa-message-facility"
                          onClick={() => facility?.email ? window.open(`mailto:${facility.email}`) : toast.info("No facility email on file.")} />
                <QuickBtn icon={StickyNote} label="Add note" testId="qa-add-note"
                          onClick={() => setTab("activity")} />
                <QuickBtn icon={FileText} label="View contract" testId="qa-view-contract"
                          onClick={() => contract ? toast.success(`Contract ${contract.title || contract.id.slice(0,8)}`) : toast.info("No contract linked.")} />
                <QuickBtn icon={DollarSign} label="Generate invoice" testId="qa-generate-invoice"
                          onClick={() => toast.info("Contract-patient invoicing routes through the facility monthly roll-up.")} />
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-md border text-xs font-semibold hover:bg-[#F8FAFC]"
                            style={{ borderColor: "#E2E8F0", background: "#FFF", color: "#0B2545" }}
                            data-testid="qa-more">
                      <MoreHorizontal className="h-3.5 w-3.5" /> More
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {!record.is_binned && (
                      <>
                        <DropdownMenuItem onClick={() => runLifecycle(record.is_archived ? "restore" : "archive")}
                                          data-testid="patient-toggle-archive">
                          <ArchiveIcon className="h-3.5 w-3.5 mr-2" />
                          {record.is_archived ? "Restore from Archive" : "Archive patient"}
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => runLifecycle("bin")} data-testid="patient-move-to-bin">
                          <Trash2 className="h-3.5 w-3.5 mr-2" /> Move to Bin
                        </DropdownMenuItem>
                      </>
                    )}
                    {record.is_binned && (
                      <>
                        <DropdownMenuItem onClick={() => runLifecycle("unbin")} data-testid="patient-restore-from-bin">
                          <RotateCcw className="h-3.5 w-3.5 mr-2" /> Restore from Bin
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={runDelete} style={{ color: "#B91C1C" }}
                                          data-testid="patient-delete-permanent">
                          <Trash2 className="h-3.5 w-3.5 mr-2" /> Delete permanently
                        </DropdownMenuItem>
                      </>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>

            {/* Tabs */}
            <Tabs value={tab} onValueChange={setTab} className="flex-1 flex flex-col overflow-hidden">
              <TabsList className="mx-5 mt-3 grid grid-cols-6 h-9" style={{ background: "#F1F5F9" }}>
                <TabsTrigger value="overview" data-testid="patient-tab-overview" className="text-[11px]">Overview</TabsTrigger>
                <TabsTrigger value="contacts" data-testid="patient-tab-contacts" className="text-[11px]">Contacts</TabsTrigger>
                <TabsTrigger value="contract" data-testid="patient-tab-contract" className="text-[11px]">Contract</TabsTrigger>
                <TabsTrigger value="standing" data-testid="patient-tab-standing" className="text-[11px]">Orders</TabsTrigger>
                <TabsTrigger value="appointments" data-testid="patient-tab-appointments" className="text-[11px]">Visits</TabsTrigger>
                <TabsTrigger value="activity" data-testid="patient-tab-activity" className="text-[11px]">Activity</TabsTrigger>
              </TabsList>

              <TabsContent value="overview" className="flex-1 overflow-y-auto p-5 space-y-4 mt-0"
                           data-testid="patient-tab-content-overview">
                <SectionCard title="Patient Information" onEdit={() => setEditing(true)}>
                  <Row icon={Phone} label="Phone" value={record.phone} />
                  <Row icon={Mail} label="Email" value={record.email}
                       link={record.email ? `mailto:${record.email}` : null} />
                  <Row icon={Calendar} label="Date of Birth"
                       value={record.dob ? `${fmtDate(record.dob)}${ageFromDob(record.dob) ? ` (${ageFromDob(record.dob)})` : ""}` : "—"} />
                  <Row icon={User} label="Gender" value={GENDER_LABEL[record.gender] || "—"} />
                  <Row icon={ShieldCheck} label="MRN" value={record.mrn} />
                  <Row icon={ShieldCheck} label="Primary Language" value={record.primary_language || "English"} />
                  <Row icon={ShieldCheck} label="Allergies" value={record.allergies || "None on file"} />
                  {record.fall_risk ? (
                    <div className="rounded-lg border p-2 text-xs mt-2"
                         style={{ borderColor: "rgba(220,38,38,0.4)", background: "rgba(220,38,38,0.06)", color: "#991B1B" }}>
                      <b>Fall risk</b>{record.mobility_notes ? ` — ${record.mobility_notes}` : ""}
                    </div>
                  ) : null}
                  {record.notes ? (
                    <div className="rounded-lg border p-2 text-xs mt-2 whitespace-pre-wrap"
                         style={{ borderColor: "rgba(212,175,55,0.35)", background: "rgba(212,175,55,0.06)", color: "#7A5E12" }}>
                      {record.notes}
                    </div>
                  ) : null}
                </SectionCard>

                <SectionCard title="Contract Coverage">
                  {(record.authorized_services || []).length === 0 ? (
                    <div className="text-xs" style={{ color: "#94A3B8" }}>
                      No authorized services on file. Add them via <b>Edit</b> above.
                    </div>
                  ) : (
                    <ul className="space-y-1.5">
                      {record.authorized_services.map((s, i) => (
                        <li key={i} className="flex items-center gap-1.5 text-sm">
                          <ShieldCheck className="h-3.5 w-3.5" style={{ color: "#0F6D5D" }} />
                          <span style={{ color: "#0B2545" }}>{s}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </SectionCard>
              </TabsContent>

              <TabsContent value="contacts" className="flex-1 overflow-y-auto p-5 space-y-4 mt-0"
                           data-testid="patient-tab-content-contacts">
                <SectionCard title="Patient Contact">
                  <Row icon={Phone} label="Phone" value={record.phone} />
                  <Row icon={Mail} label="Email" value={record.email}
                       link={record.email ? `mailto:${record.email}` : null} />
                  <Row icon={MapPin} label="Address" value={record.address} />
                </SectionCard>

                <SectionCard title="Primary Facility Contact">
                  <Row icon={User} label="Name"
                       value={record.primary_facility_contact_name || facility?.contact_person || "—"} />
                  <Row icon={Phone} label="Phone"
                       value={record.primary_facility_contact_phone || facility?.phone || "—"} />
                  <Row icon={Mail} label="Facility Email"
                       value={facility?.email || "—"}
                       link={facility?.email ? `mailto:${facility.email}` : null} />
                </SectionCard>

                <SectionCard title="Care Team">
                  <Row icon={User} label="Assigned Staff"
                       value={record.assigned_staff_name || "—"} />
                </SectionCard>
              </TabsContent>

              <TabsContent value="contract" className="flex-1 overflow-y-auto p-5 space-y-4 mt-0"
                           data-testid="patient-tab-content-contract">
                <SectionCard title="Assigned Facility">
                  {facility ? (
                    <>
                      <Row icon={Building2} label="Facility" value={facility.facility_name} />
                      <Row icon={ShieldCheck} label="Status" value={facility.status} />
                      <Row icon={Phone} label="Phone" value={facility.phone} />
                      <Row icon={Mail} label="Email" value={facility.email} />
                      <Row icon={MapPin} label="Address" value={facility.address} />
                    </>
                  ) : (
                    <div className="text-xs" style={{ color: "#94A3B8" }}>Facility record missing.</div>
                  )}
                </SectionCard>

                <SectionCard title="Contract">
                  <Row icon={FileText} label="Contract Number" value={record.contract_number} />
                  {contract ? (
                    <>
                      <Row icon={FileText} label="Contract Title" value={contract.title} />
                      <Row icon={ShieldCheck} label="Status" value={contract.status} />
                      <Row icon={Calendar} label="Start Date" value={fmtDate(contract.start_date)} />
                      <Row icon={Calendar} label="End Date" value={fmtDate(contract.end_date)} />
                      {typeof contract.value === "number" ? (
                        <Row icon={DollarSign} label="Contract Value" value={`$${contract.value.toLocaleString()}`} />
                      ) : null}
                    </>
                  ) : (
                    <div className="text-xs" style={{ color: "#94A3B8" }}>
                      No contract linked yet. Attach one via <b>Edit</b> and paste the contract ID.
                    </div>
                  )}
                </SectionCard>
              </TabsContent>

              <TabsContent value="standing" className="flex-1 overflow-y-auto p-5 space-y-4 mt-0"
                           data-testid="patient-tab-content-standing">
                <StandingOrdersPanel
                  facilityId={record.facility_id}
                  facilityName={record.facility_name}
                  patientId={record.id}
                  patientName={record.full_name}
                  onChanged={() => { /* refresh on activity change if needed */ }}
                />
              </TabsContent>

              <TabsContent value="appointments" className="flex-1 overflow-y-auto p-5 space-y-3 mt-0"
                           data-testid="patient-tab-content-appointments">
                <PatientAppointmentsList patientId={record.id} />
              </TabsContent>

              <TabsContent value="activity" className="flex-1 overflow-y-auto p-5 space-y-4 mt-0"
                           data-testid="patient-tab-content-activity">
                <SectionCard title="Add note">
                  <div className="space-y-2">
                    <Textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)}
                              placeholder="Staff-only note about this patient…" data-testid="patient-note-input" />
                    <div className="flex justify-end">
                      <Button size="sm" onClick={submitNote} disabled={savingNote || !note.trim()}
                              className="h-8 text-xs" style={{ background: "#7A5E12", color: "#FFF" }}
                              data-testid="patient-note-submit">
                        {savingNote ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <StickyNote className="h-3.5 w-3.5 mr-1" />}
                        Save note
                      </Button>
                    </div>
                  </div>
                </SectionCard>

                <SectionCard title="Activity Timeline">
                  {(record.activity_log || []).length === 0 ? (
                    <div className="text-xs" style={{ color: "#94A3B8" }}>No activity yet.</div>
                  ) : (
                    <ol className="space-y-2" data-testid="patient-activity-list">
                      {[...(record.activity_log || [])].reverse().map((e, i) => (
                        <li key={i} className="rounded-lg border p-2.5" style={{ borderColor: "#E2E8F0" }}>
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-[10px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded"
                                  style={{ background: "rgba(11,37,69,0.06)", color: "#0B2545" }}>
                              {(e.action || "event").replace(/_/g, " ")}
                            </span>
                            <span className="text-[10px]" style={{ color: "#94A3B8" }}>{fmtDateTime(e.ts)}</span>
                          </div>
                          <div className="mt-1 text-xs" style={{ color: "#334155" }}>{e.summary}</div>
                          {e.actor ? (
                            <div className="mt-0.5 text-[10px]" style={{ color: "#94A3B8" }}>by {e.actor}</div>
                          ) : null}
                        </li>
                      ))}
                    </ol>
                  )}
                </SectionCard>
              </TabsContent>
            </Tabs>

            {/* Edit sheet */}
            {editing && (
              <div className="absolute inset-0 bg-white flex flex-col z-20" data-testid="patient-edit-form">
                <div className="px-5 py-3 border-b flex items-center justify-between" style={{ borderColor: "#F1F5F9" }}>
                  <div className="text-sm font-semibold" style={{ color: "#0B2545" }}>Edit patient</div>
                  <button onClick={() => setEditing(false)} className="text-xs" style={{ color: "#64748B" }}>Cancel</button>
                </div>
                <div className="flex-1 overflow-y-auto p-5 space-y-3">
                  <EditField label="Full name" testId="edit-full_name"
                             value={form.full_name} onChange={(v) => setForm((f) => ({ ...f, full_name: v }))} />
                  <div className="grid grid-cols-2 gap-3">
                    <EditField label="Phone" testId="edit-phone" value={form.phone} onChange={(v) => setForm((f) => ({ ...f, phone: v }))} />
                    <EditField label="Email" testId="edit-email" value={form.email} type="email" onChange={(v) => setForm((f) => ({ ...f, email: v }))} />
                  </div>
                  <EditField label="Address" testId="edit-address" value={form.address} onChange={(v) => setForm((f) => ({ ...f, address: v }))} />
                  <div className="grid grid-cols-2 gap-3">
                    <EditField label="Date of Birth" testId="edit-dob" type="date" value={form.dob} onChange={(v) => setForm((f) => ({ ...f, dob: v }))} />
                    <EditField label="MRN" testId="edit-mrn" value={form.mrn} onChange={(v) => setForm((f) => ({ ...f, mrn: v }))} />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Gender</Label>
                      <Select value={form.gender || ""} onValueChange={(v) => setForm((f) => ({ ...f, gender: v }))}>
                        <SelectTrigger className="mt-1 h-9" data-testid="edit-gender"><SelectValue placeholder="—" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="male">Male</SelectItem>
                          <SelectItem value="female">Female</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                          <SelectItem value="prefer_not_to_say">Prefer not to say</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <EditField label="Primary Language" testId="edit-primary_language"
                               value={form.primary_language} onChange={(v) => setForm((f) => ({ ...f, primary_language: v }))} />
                  </div>
                  <EditField label="Allergies" testId="edit-allergies"
                             value={form.allergies} onChange={(v) => setForm((f) => ({ ...f, allergies: v }))} />
                  <div>
                    <Label className="text-xs">Notes</Label>
                    <Textarea rows={3} value={form.notes} className="mt-1"
                              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                              data-testid="edit-notes" />
                  </div>
                  <EditField label="Contract number" testId="edit-contract_number"
                             value={form.contract_number} onChange={(v) => setForm((f) => ({ ...f, contract_number: v }))} />
                  <div>
                    <Label className="text-xs">Authorized services (comma-separated)</Label>
                    <Input className="mt-1" value={form.authorized_services}
                           onChange={(e) => setForm((f) => ({ ...f, authorized_services: e.target.value }))}
                           placeholder="Phlebotomy, Wellness Panels, Wound Care"
                           data-testid="edit-authorized_services" />
                  </div>
                </div>
                <div className="px-5 py-3 border-t flex justify-end gap-2" style={{ borderColor: "#F1F5F9" }}>
                  <Button variant="outline" onClick={() => setEditing(false)} className="h-9">Cancel</Button>
                  <Button onClick={saveEdits} disabled={savingField}
                          className="h-9" style={{ background: "#0B2545", color: "#FFF" }}
                          data-testid="patient-edit-save">
                    {savingField ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
                    Save changes
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}

// ---------------------------------------------------------------------------
// Small helper atoms
// ---------------------------------------------------------------------------
const QuickBtn = ({ icon: Icon, label, onClick, testId, accent }) => (
  <button
    onClick={onClick}
    className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-md border text-xs font-semibold hover:bg-[#F8FAFC] transition"
    style={{ borderColor: "#E2E8F0", background: "#FFF", color: "#0B2545" }}
    data-testid={testId}
  >
    <Icon className="h-3.5 w-3.5" style={{ color: accent || "#2563EB" }} />
    {label}
  </button>
);

const SectionCard = ({ title, children, onEdit }) => (
  <div className="rounded-xl border p-3.5" style={{ borderColor: "#E2E8F0", background: "#FFF" }}>
    <div className="flex items-center justify-between mb-2">
      <div className="text-[11px] uppercase tracking-wider font-bold" style={{ color: "#0B2545" }}>{title}</div>
      {onEdit ? (
        <button onClick={onEdit} className="text-[11px] font-semibold" style={{ color: "#2563EB" }}
                data-testid={`edit-section-${title.toLowerCase().replace(/\s+/g, "-")}`}>
          Edit
        </button>
      ) : null}
    </div>
    <div className="space-y-1.5">{children}</div>
  </div>
);

const Row = ({ icon: Icon, label, value, link }) => (
  <div className="flex items-start gap-2 text-sm">
    <Icon className="h-3.5 w-3.5 mt-0.5 shrink-0" style={{ color: "#94A3B8" }} />
    <div className="flex-1 min-w-0">
      <div className="text-[10px] uppercase tracking-wider" style={{ color: "#94A3B8" }}>{label}</div>
      {link ? (
        <a href={link} className="text-sm break-words" style={{ color: "#2563EB" }}>{value || "—"}</a>
      ) : (
        <div className="text-sm break-words" style={{ color: "#0B2545" }}>{value || "—"}</div>
      )}
    </div>
  </div>
);

const EditField = ({ label, value, onChange, type = "text", testId }) => (
  <div>
    <Label className="text-xs">{label}</Label>
    <Input type={type} value={value || ""} onChange={(e) => onChange(e.target.value)}
           className="mt-1 h-9" data-testid={testId} />
  </div>
);

const PatientAppointmentsList = ({ patientId }) => {
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    listPatientAppointments(patientId)
      .then((r) => { if (!cancelled) setRows(r.appointments || []); })
      .catch(() => { /* toast handled globally */ })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [patientId]);

  if (loading) return (
    <div className="grid place-items-center py-10">
      <Loader2 className="h-5 w-5 animate-spin" style={{ color: "#0B2545" }} />
    </div>
  );
  if (!rows.length) return (
    <div className="rounded-xl border-2 border-dashed p-6 text-center text-sm"
         style={{ borderColor: "#E2E8F0", color: "#64748B" }} data-testid="patient-appointments-empty">
      No appointments yet. Create a Standing Order to schedule recurring visits.
    </div>
  );
  return (
    <div className="space-y-2" data-testid="patient-appointments-list">
      {rows.map((r) => (
        <div key={r.id} className="rounded-xl border p-3 bg-white" style={{ borderColor: "#E2E8F0" }}
             data-testid={`patient-appointment-row-${r.id}`}>
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0">
              <div className="text-sm font-semibold" style={{ color: "#0B2545" }}>
                {(r.services || []).join(", ") || "Service"}
              </div>
              <div className="text-[11px]" style={{ color: "#64748B" }}>
                {fmtDateTime(r.appointment_at)}
                {r.assigned_staff_name ? ` · ${r.assigned_staff_name}` : ""}
                {r.request_id ? ` · #${r.request_id}` : ""}
              </div>
            </div>
            <span className="text-[9px] uppercase font-bold tracking-wider px-2 py-0.5 rounded"
                  style={{ background: "rgba(11,37,69,0.06)", color: "#0B2545" }}>
              {r.status || r.dispatch_status || "scheduled"}
            </span>
          </div>
        </div>
      ))}
    </div>
  );
};
